/* Custom metafield-driven breadcrumbs (WSL Store)
   Collapses overflowing middle crumbs into an ellipsis micro-dropdown so the
   trail always stays on one line. Progressive enhancement: with JS off the
   trail renders in full and is clipped by CSS, never wrapped. */
(function () {
  if (customElements.get('breadcrumb-trail')) return;

  class BreadcrumbTrail extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-bc-list]');
      this.ellipsisItem = this.querySelector('[data-bc-ellipsis]');
      this.button = this.querySelector('[data-bc-toggle]');
      this.dropdown = this.querySelector('[data-bc-dropdown]');
      if (!this.list || !this.ellipsisItem || !this.button || !this.dropdown) return;

      // Remember where each collapsible crumb lives so it can be put back.
      this.collapsible = Array.from(this.querySelectorAll('[data-bc-collapsible]')).map(function (el) {
        return { el: el, next: el.nextElementSibling };
      });
      if (!this.collapsible.length) {
        this.ellipsisItem.hidden = true;
        return;
      }

      // Below this viewport width the middle level is always collapsed: a
      // parent title squeezed into ~14 characters is unreadable, so the
      // dropdown is a better trade than truncation on phones.
      this.collapseBelow = parseInt(this.getAttribute('data-bc-collapse-below'), 10) || 768;

      this.onToggle = this.onToggle.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.classList.add('bc-ready');
      this.button.addEventListener('click', this.onToggle);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.schedule.bind(this));
        this.observer.observe(this);
      } else {
        this.onResize = this.schedule.bind(this);
        window.addEventListener('resize', this.onResize);
      }

      // Fonts can land after first paint and change the measurement.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.schedule.bind(this));
      }
      this.fit();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.observer) this.observer.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    schedule() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.fit.bind(this));
    }

    /* Expand everything, then hide middle crumbs one by one (nearest the top
       of the trail first) until the row fits on a single line. */
    fit() {
      this.close();
      this.expandAll();

      var forceCollapse = window.innerWidth < this.collapseBelow;

      var i = 0;
      while ((forceCollapse || this.overflows()) && i < this.collapsible.length) {
        this.collapse(this.collapsible[i]);
        i += 1;
      }
      var collapsedCount = i;
      this.ellipsisItem.hidden = collapsedCount === 0;
      this.button.setAttribute(
        'aria-label',
        collapsedCount === 1 ? 'Show 1 hidden breadcrumb level' : 'Show ' + collapsedCount + ' hidden breadcrumb levels'
      );
    }

    overflows() {
      // 1px of slack absorbs sub-pixel layout rounding.
      return this.list.scrollWidth - this.list.clientWidth > 1;
    }

    expandAll() {
      var self = this;
      this.collapsible.forEach(function (entry) {
        if (entry.el.parentNode === self.list) return;
        if (entry.next && entry.next.parentNode === self.list) {
          self.list.insertBefore(entry.el, entry.next);
        } else {
          self.list.appendChild(entry.el);
        }
        entry.el.hidden = false;
        if (entry.clone) {
          entry.clone.remove();
          entry.clone = null;
        }
      });
      this.ellipsisItem.hidden = true;
    }

    collapse(entry) {
      var link = entry.el.querySelector('a');
      var item = document.createElement('li');
      var anchor = document.createElement('a');
      anchor.href = link ? link.getAttribute('href') : '#';
      anchor.textContent = (link ? link.textContent : entry.el.textContent).trim();
      item.appendChild(anchor);
      this.dropdown.appendChild(item);
      entry.clone = item;
      entry.el.remove();
    }

    onToggle(event) {
      event.preventDefault();
      if (this.button.getAttribute('aria-expanded') === 'true') {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.dropdown.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      var first = this.dropdown.querySelector('a');
      if (first) first.focus({ preventScroll: true });
    }

    close() {
      if (!this.dropdown) return;
      this.dropdown.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.button.getAttribute('aria-expanded') !== 'true') return;
      this.close();
      this.button.focus();
    }
  }

  customElements.define('breadcrumb-trail', BreadcrumbTrail);
})();
